import type { PageContext } from '../lib/types';
import { createElement, lazy, Suspense } from 'react';
import { usesDesignV2 } from '../features/design-v2/rollout';
const AssetsPage = lazy(() => import('../features/design-v2/AssetsPage').then(module => ({ default: module.AssetsPage })));
// Assets (IA §14–16, §27–28, seed §40–42): ONE global resource database. Filters, not folders.
import { html } from '../lib/dom';
import { icon } from '../components/icons';
import { assetThumb } from '../components/thumb';
import { pageHead, select, searchInput, assetCard, table, status, typeTag, empty, favButton } from '../components/ui';
import { openModal, confirmModal } from '../components/modal';
import { listAssets, deleteAssets, typeCounts, assetListState, loadMoreAssets, forgetAssets, TYPE_GROUPS, SCOPES, SCOPE_LABEL, ownerLabel, usage, assetCreators, ASSET_STATUSES } from '../api/library';
import { loadMoreMarker, bindLoadMore } from '../components/load-more';
import { listBrands, listWorkspaces, listProjects, getWorkspace, currentUser } from '../api/workspace';
import { toast } from '../lib/dom';
import { relTime, plural } from '../lib/format';

const visibleGroups = TYPE_GROUPS.filter(([key]: any) => ['all', 'images', 'video', 'text', 'audio', '3d', 'templates'].includes(key));

const uploadTypes = [['IMAGE', 'Image'], ['VIDEO', 'Video'], ['AUDIO', 'Audio'], ['TEXT', 'Text'], ['MODEL_3D', '3D Model']];

function fileDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === 'string'
      ? resolve(reader.result.replace(/^data:;base64,/, 'data:application/octet-stream;base64,'))
      : reject(new Error('Could not read the file.'));
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.readAsDataURL(file);
  });
}

export function openAssetUpload(ctx: PageContext) {
  const { project: projectId, brand: brandId, workspace } = ctx.query;
  const workspaceId = ctx.workspaceId || workspace;
  const scope = projectId ? 'PROJECT' : brandId ? 'BRAND' : workspaceId ? 'WORKSPACE' : 'ORGANIZATION';
  openModal({
    title: 'Add asset to Assets',
    body: html`<form class="form" onsubmit="return false">
      <div class="field"><label>Name</label><input class="input" name="name" placeholder="e.g. Koloblox Winter Pose" autofocus></div>
      <div class="form-row">
        <div class="field"><label>Type</label><select class="select" name="type">${uploadTypes.map(([value, label]) => html`<option value="${value}">${label}</option>`)}</select></div>
      </div>
      <div class="field"><label>File</label><input class="input" type="file" name="file" required><span class="muted" style="font-size:11px">Images, videos, audio, text and 3D models up to 40 MB.</span></div>
      <div class="banner error" data-upload-error hidden></div>
    </form>`,
    foot: html`<button class="btn ghost" data-close>Cancel</button><button class="btn primary" data-create>Add asset</button>`,
    onMount(m?: any, close?: any) {
      const f = m.querySelector('form');
      const fileInput = f.elements.namedItem('file');
      fileInput.addEventListener('change', () => {
        const file = fileInput.files?.[0];
        if (!file) return;
        if (!f.elements.name.value.trim()) f.elements.name.value = file.name;
        const kind = file.type.split('/')[0];
        const inferred = ({ image:'IMAGE', video:'VIDEO', audio:'AUDIO', text:'TEXT', model:'MODEL_3D' } as Record<string, string>)[kind]
          || (/\.(?:glb|gltf|obj|fbx|usdz|stl|ply)$/i.test(file.name) ? 'MODEL_3D' : '');
        f.elements.type.value = inferred;
      });
      const button = m.querySelector('[data-create]');
      const buttonLabel = button.innerHTML;
      button.addEventListener('click', async () => {
        const error = m.querySelector('[data-upload-error]');
        error.hidden = true;
        // form.name is the form's own name attribute, not the input — go through .elements
        const name = f.elements.name.value.trim();
        if (!name) { f.elements.name.focus(); return; }
        const file = fileInput.files?.[0];
        if (!file) { fileInput.focus(); return; }
        if (!f.elements.type.value) { error.textContent = 'Choose an image, video, audio, text or 3D model file.'; error.hidden = false; return; }
        if (file.size > 40 * 1024 * 1024) { error.textContent = 'Choose a file smaller than 40 MB.'; error.hidden = false; return; }
        button.disabled = true;
        button.textContent = 'Uploading…';
        try {
          const response = await fetch('/api/v1/uploads', {
            method:'POST', headers:{ 'Content-Type':'application/json' },
            body:JSON.stringify({ name, assetType:f.elements.type.value, scope,
              brandId:scope === 'BRAND' ? brandId : undefined,
              workspaceId:scope === 'WORKSPACE' ? workspaceId : undefined,
              projectId:scope === 'PROJECT' ? projectId : undefined,
              dataUrl:await fileDataUrl(file) }),
          });
          const result = await response.json().catch(() => ({}));
          if (!response.ok || !result.assetId) throw new Error(result.error || 'Upload failed. Please try again.');
          forgetAssets();
          close(); location.hash = `/assets/${result.assetId}`;
        } catch (cause: any) {
          error.textContent = cause?.message || 'Upload failed. Please try again.';
          error.hidden = false;
        } finally {
          button.disabled = false;
          button.innerHTML = buttonLabel;
        }
      });
    },
  });
}

export function render(ctx: PageContext) {
  if (usesDesignV2(ctx)) {
    return { title: 'Assets', content: createElement(Suspense, { fallback: createElement('div', { role:'status', className:'page' }, 'Loading assets…') }, createElement(AssetsPage, { context:ctx })) };
  }
  const q = ctx.query;
  const wsCtx = ctx.workspaceId && q.all !== '1' ? getWorkspace(ctx.workspaceId) : null;
  const filters: any = { family: q.family, q: q.q, scope: q.scope, brandId: q.brand, workspaceId: wsCtx?.id || q.workspace, projectId: q.project, status: q.status, creator: q.creator, used: q.used, sort: q.sort };
  const group = visibleGroups.some(([key]: any) => key === q.group) ? q.group : 'all';
  const rows = listAssets({ ...filters, group });
  const state = assetListState({ ...filters, group });
  const counts = typeCounts(filters);
  const total = counts[group];
  const layout = q.layout || 'grid';
  const hasFilters = ['family', 'q', 'scope', 'brand', 'workspace', 'project', 'status', 'creator', 'used'].some((k?: any) => q[k]);

  const body = html`<div class="page">
    ${pageHead({ title: 'Assets', lede: 'Reusable resources in one shared library — media, prompts, characters, models, effects, and templates. Generated outputs belong here when saved for reuse.', actions: html`<div class="row">${currentUser()?.role === 'admin' && rows.length ? html`<button class="btn danger" data-delete-assets>${icon('trash')}Delete ${rows.length} asset${rows.length === 1 ? '' : 's'}</button>` : ''}<a class="btn" href="#/assets/templates/new${q.project ? '?projectId=' + encodeURIComponent(q.project) : ''}">${icon('plus')}New Remotion template</a><button class="btn primary" data-upload>${icon('plus')}Add asset</button></div>` })}
    ${wsCtx ? html`<div class="banner info">${icon('workspaces')}<div style="flex:1">Showing assets <strong>available in ${wsCtx.name}</strong>.</div><button class="btn sm ghost" data-param="all" data-value="1">Show all</button></div>` : ''}
    <div class="type-chips" role="tablist" aria-label="Asset type">${visibleGroups.map(([k, l]: any) => html`<button class="${group === k ? 'on' : ''}" data-param="group" data-value="${k === 'all' ? '' : k}">${l}<span class="n">${counts[k] ?? '…'}</span></button>`)}</div>
    <div class="toolbar">
      ${searchInput(q.q, 'Search resources…')}
      ${select('family', [['helloiam-emojis', 'HelloIAM Emojis'], ['helloiam-cards', 'HelloIAM Cards']], q.family, { placeholder: 'Asset set' })}
      ${select('scope', SCOPES.map((s?: any) => [s, SCOPE_LABEL[s]]), q.scope, { placeholder: 'Scope' })}
      ${select('brand', listBrands().map((b?: any) => [b.id, b.name]), q.brand, { placeholder: 'Brand' })}
      ${wsCtx ? '' : select('workspace', listWorkspaces().map((w?: any) => [w.id, w.name]), q.workspace, { placeholder: 'Workspace' })}
      ${select('project', listProjects().map((p?: any) => [p.id, p.name]), q.project, { placeholder: 'Project' })}
      ${select('status', ASSET_STATUSES.map((s?: any) => [s, s]), q.status, { placeholder: 'Status' })}
      ${select('creator', assetCreators().map((u?: any) => [u.id, u.name]), q.creator, { placeholder: 'Creator' })}
      ${select('used', [['used', 'Used'], ['unused', 'Unused']], q.used, { placeholder: 'Used / Unused' })}
      ${hasFilters ? html`<a class="btn sm ghost" href="#/assets">Clear</a>` : ''}
      <span class="spacer"></span>
      ${select('sort', [['updated', 'Recently updated'], ['name', 'Name'], ['usage', 'Most used']], q.sort || 'updated')}
      <div class="seg" role="group" aria-label="Layout"><button class="${layout === 'grid' ? 'on' : ''}" data-param="layout" data-value="grid">${icon('grid')}</button><button class="${layout === 'table' ? 'on' : ''}" data-param="layout" data-value="table">${icon('list')}</button></div>
    </div>
    <div class="mono muted" style="font-size:11px">${total === undefined ? 'Counting assets…' : plural(total, 'asset')}</div>
    ${!rows.length ? (state.done ? empty('No assets match these filters.', html`<a class="btn sm" href="#/assets">Clear filters</a>`) : '')
      : layout === 'grid'
        ? html`<div class="grid auto-sm">${rows.map((a?: any) => { const u = usage(a.id); return assetCard(a, `${ownerLabel(a)}${u.flows ? ` · ${u.flows} flows` : ''}`); })}</div>`
        : html`<div class="card">${table([
          { label: 'Asset', render: (a?: any) => html`<div class="cell-entity">${assetThumb(a)}<strong style="font-weight:500">${a.name}</strong></div>` },
          { label: 'Type', render: (a?: any) => typeTag(a.type) },
          { label: 'Used in', render: (a?: any) => { const u = usage(a.id); return u.loading ? html`<span class="muted">${u.error ? 'Unavailable' : 'Loading…'}</span>` : u.nodes ? `${plural(u.flows, 'flow')} · ${plural(u.projects, 'project')}` : html`<span class="muted">Unused</span>`; } },
          { label: 'Status', render: (a?: any) => status(a.status.toLowerCase(), a.status) },
          { label: 'Ver.', num: true, render: (a?: any) => 'v' + a.current_version },
          { label: 'Updated', render: (a?: any) => relTime(a.updated_at) },
          { label: '', render: (a?: any) => favButton('asset', a.id) },
        ], rows, { rowHref: (a?: any) => a.data?.origin === 'remotion-template' || a.origin === 'remotion-template' ? `#/assets/templates/${a.id}/edit` : `#/assets/${a.id}` })}</div>`}
    ${loadMoreMarker(state, 'Loading assets…')}
  </div>`;

  return { title: 'Assets', body, mount: (root?: any) => {
    root.querySelector('[data-upload]')?.addEventListener('click', () => openAssetUpload(ctx));
    const unbind = bindLoadMore(root, state, () => loadMoreAssets({ ...filters, group }));
    root.querySelector('[data-delete-assets]')?.addEventListener('click', () => confirmModal({
      title: `Delete ${rows.length} asset${rows.length === 1 ? '' : 's'}?`,
      text: 'This removes the assets loaded on this page from the library. Cover images and execution results are kept; other uploaded files are permanently deleted. Scroll down first to include more.',
      confirmLabel: `Delete ${rows.length} asset${rows.length === 1 ? '' : 's'}`, danger: true,
      onConfirm: async () => {
        const count = await deleteAssets(rows.map((asset?: any) => asset.id));
        forgetAssets();
        toast(`${count} asset${count === 1 ? '' : 's'} deleted`);
        ctx.rerender();
      },
    }));
    return unbind;
  } };
}
